import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CompanyPayload, UserPayload } from "types/auth.api";
import {
  businessProfileSetup,
  deleteUser,
  forgotPassword,
  loginUser,
  logoutUser,
  profileSetup,
  registerUser,
  resendEmailVerification,
  getUserDetails,
  initiateImpersonate,
  leaveImpersonate,
} from "./thunk";
import { ApiResponse } from "types/ApiResponse";
import { setAuthorization } from "helpers/api_helper";
import { REHYDRATE } from "redux-persist";

interface RegisterState {
  message: string | null;
  isLoading: boolean;
  user: UserPayload | null;
  success: boolean;
  error: boolean;
  completedTourGuides: number[];
  isImpersonated?: boolean;
  revenueBillingInfo?: {
    info: any;
    willRenew: boolean | undefined;
  };
}

export const initialState: RegisterState = {
  message: null,
  isLoading: false,
  user: null,
  success: false,
  error: false,
  completedTourGuides: [],
  isImpersonated: false,
  revenueBillingInfo: {
    info: null,
    willRenew: undefined,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateUserPayload: (state, action: PayloadAction<Partial<UserPayload>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },

    deleteUserPayload: (state) => {
      if (state.user) {
        state.user = null;
      }
    },
    setCompletedTourGuidesFromStore: (state, action: PayloadAction<number>) => {
      state.completedTourGuides.push(action.payload);
    },
    setRevenueBillingInfo: (
      state,
      action: PayloadAction<{ info: any; willRenew: boolean }>
    ) => {
      state.revenueBillingInfo = {
        info: action.payload.info,
        willRenew: action.payload.willRenew,
      };
    },
  },

  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: any) => {
      // Restore authorization header when state is rehydrated
      if (action.payload?.auth?.user?.access_token) {
        setAuthorization(action.payload.auth.user.access_token);
      }
    });
    //======Register=======//

    // ---------- Pending ----------
    builder.addCase(registerUser.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(registerUser.fulfilled, (state) => {
      state.isLoading = false;
      state.error = false;
      state.success = true;
    });

    // ---------- Rejected ----------
    builder.addCase(registerUser.rejected, (state, action) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
      state.user = null;
    });

    //======Login=======//

    // ---------- Pending ----------
    builder.addCase(loginUser.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(
      loginUser.fulfilled,
      (state, action: PayloadAction<ApiResponse<UserPayload>>) => {
        state.isLoading = false;
        state.error = false;
        state.success = true;
        setAuthorization(action?.payload?.payload?.access_token);
        if (
          action?.payload?.payload?.is_email_verified &&
          action?.payload?.payload?.is_active
        ) {
          state.user = action.payload.payload;
        } else {
          state.user = null;
        }
      }
    );

    // ---------- Rejected ----------
    builder.addCase(loginUser.rejected, (state, action) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
      state.user = null;
    });

    //======Resend Email Verification=======//

    // ---------- Pending ----------
    builder.addCase(resendEmailVerification.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(resendEmailVerification.fulfilled, (state) => {
      state.isLoading = false;
      state.error = false;
      state.success = true;
      setAuthorization();
    });

    // ---------- Rejected ----------
    builder.addCase(resendEmailVerification.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
      state.user = null;
    });

    //======Logout=======//

    // ---------- Pending ----------
    builder.addCase(logoutUser.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.isLoading = false;
      state.error = false;
      state.success = true;
      state.user = null;
    });

    // ---------- Rejected ----------
    builder.addCase(logoutUser.rejected, (state, action) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
      state.user = null;
    });

    //======Profile Setup=======//

    // ---------- Pending ----------
    builder.addCase(profileSetup.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(
      profileSetup.fulfilled,
      (state, action: PayloadAction<ApiResponse<UserPayload>>) => {
        state.isLoading = false;
        state.error = false;
        state.success = true;
        state.user = { ...state.user, ...action?.payload?.payload };
      }
    );

    // ---------- Rejected ----------
    builder.addCase(profileSetup.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
    });

    //======Forgot Password=======//

    // ---------- Pending ----------
    builder.addCase(forgotPassword.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.isLoading = false;
      state.error = false;
      state.success = true;
    });

    // ---------- Rejected ----------
    builder.addCase(forgotPassword.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
    });

    //======Business Profile Setup=======//

    // ---------- Pending ----------
    builder.addCase(businessProfileSetup.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(
      businessProfileSetup.fulfilled,
      (state, action: PayloadAction<ApiResponse<CompanyPayload>>) => {
        state.isLoading = false;
        state.error = false;
        state.success = true;
        if (state?.user) {
          state.user.company = action?.payload?.payload;
          state.user.is_company_profile_setup = true;
        }
      }
    );

    // ---------- Rejected ----------
    builder.addCase(businessProfileSetup.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
    });

    //======DELETE USER=======//

    // ---------- Pending ----------
    builder.addCase(deleteUser.pending, (state) => {
      state.isLoading = true;
      state.error = false;
      state.success = false;
    });

    // ---------- Fulfilled ----------
    builder.addCase(deleteUser.fulfilled, (state) => {
      state.isLoading = false;
      state.error = false;
      state.success = true;
      state.user = null;
    });

    // ---------- Rejected ----------
    builder.addCase(deleteUser.rejected, (state) => {
      state.isLoading = false;
      state.error = true;
      state.success = false;
      state.user = null;
    });

    //======USER DETAILS=======//
    builder
      .addCase(getUserDetails.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.success = false;
      })
      .addCase(getUserDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user = { ...state.user, ...action.payload.payload };
        }
        state.success = true;
      })
      .addCase(getUserDetails.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.success = false;
      });

    //======IMPERSONATE USER=======//

    //Initiate Impersonate
    builder
      .addCase(initiateImpersonate.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.success = false;
      })
      .addCase(initiateImpersonate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        const payload = action.payload.payload;
        state.user = {
          id: payload.user.id,
          name: payload.user.name,
          email: payload.user.email,
          phone: payload.user.phone,
          avatar: payload.user.avatar,
          role: payload.user.role,
          is_email_verified: payload.user.is_email_verified,
          email_notification_enabled: payload.user.email_notification_enabled,
          push_notification_enabled: payload.user.push_notification_enabled,
          is_profile_setup: payload.user.is_profile_setup,
          is_company_profile_setup: payload.user.is_company_profile_setup,
          is_subscription_active: payload.user.is_subscription_active,
          created_at: payload.user.created_at ?? "",
          updated_at: payload.user.updated_at ?? "",
          access_token: payload.access_token,
          company: null,
          is_businessSetUpCompleted: false,
          is_trial_period: payload.user.is_trial_period,
          subscription_ended_at: payload.user.subscription_ended_at,
          subscription_amount: payload.user.subscription_amount,
          is_active: payload.user.is_active,
          hasBankAccountDetailAdded: payload.user.hasBankAccountDetailAdded,
          hasSignatureAdded: payload.user.hasSignatureAdded,
          is_team_member: payload.user.is_team_member,
        };
        setAuthorization(payload.access_token);
        state.isImpersonated = true;
      })
      .addCase(initiateImpersonate.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.success = false;
        state.isImpersonated = false;
      })

      //Leave Impersonate
      .addCase(leaveImpersonate.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.success = false;
      })
      .addCase(leaveImpersonate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = true;
        const payload = action.payload.payload;
        state.user = {
          id: payload.user.id,
          name: payload.user.name,
          email: payload.user.email,
          phone: payload.user.phone,
          avatar: payload.user.avatar,
          role: payload.user.role,
          is_email_verified: payload.user.is_email_verified,
          email_notification_enabled: payload.user.email_notification_enabled,
          push_notification_enabled: payload.user.push_notification_enabled,
          is_profile_setup: payload.user.is_profile_setup,
          is_company_profile_setup: payload.user.is_company_profile_setup,
          is_subscription_active: payload.user.is_subscription_active,
          created_at: payload.user.created_at ?? "",
          updated_at: payload.user.updated_at ?? "",
          access_token: payload.access_token,
          company: null,
          is_businessSetUpCompleted: false,
          is_trial_period: payload.user.is_trial_period,
          subscription_ended_at: payload.user.subscription_ended_at,
          subscription_amount: payload.user.subscription_amount,
          is_active: payload.user.is_active,
          hasBankAccountDetailAdded: payload.user.hasBankAccountDetailAdded,
          hasSignatureAdded: payload.user.hasSignatureAdded,
          is_team_member: payload.user.is_team_member,
        };
        setAuthorization(payload.access_token);
        state.isImpersonated = false;
      })
      .addCase(leaveImpersonate.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.success = false;
        state.isImpersonated = true;
      });
  },
});

export const {
  updateUserPayload,
  deleteUserPayload,
  setCompletedTourGuidesFromStore,
  setRevenueBillingInfo,
} = authSlice.actions;

export default authSlice.reducer;
